import React, { useEffect, useState } from 'react';
import { client } from '../../client';
import { Spinner } from '../Spinner';
import site from '../../site.json';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, SearchIcon, LocationMarkerIcon } from '@heroicons/react/outline';
import { searchPath } from '../../utils/searchPath';

// Shared hero for refreshed boards (RefugeeJobLeads design, 2026-10-02): tinted band, bold headline,
// one rounded search card, popular searches, plain promises and a three-step how-it-works.
// Words come from the board's site.json "home"; the picture is passed in by the board.
const PROMISES = ['Free for job seekers', 'No account needed', 'Apply in minutes'];
// One-tap searches. The list comes from this board's open jobs (/api/jobs?name=popular: words with
// 3+ open listings, busiest first), so a chip never leads to an empty page. Nothing is shown until
// it loads, and nothing at all if the board has too few jobs.
const STEPS = [
  ['Search', 'Look for jobs by title, city or province across Canada.'],
  ['Apply', 'Send your résumé straight to the employer from the job page.'],
  ['Start', 'The employer contacts you directly about next steps.'],
];

export default function ModernHero({ image }) {
  const home = site.home || {};
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [popular, setPopular] = useState(null); // null while loading
  const navigate = useNavigate();

  useEffect(() => {
    client
      .fetch({ name: 'popular' })
      .then((terms) => setPopular(Array.isArray(terms) ? terms.filter((t) => typeof t === 'string').slice(0, 7) : []))
      .catch(() => setPopular([]));
  }, []);

  function handleSearch(e) {
    e.preventDefault();
    navigate(searchPath(searchTerm, searchLocation));
  }

  return (
    <>
      <section className="relative overflow-hidden bg-tint text-gray-700">
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/20" />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-12 px-5 py-16 md:flex-row md:py-24">
          <div className="w-full md:w-3/5">
            <p className="inline-flex rounded-full bg-white px-3 py-1 text-sm font-semibold text-primary-dark shadow-sm ring-1 ring-primary/30">
              {home.eyebrow}
            </p>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-5xl">
              {home.headline} <span className="text-primary-dark">{home.highlight}</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed">{home.intro}</p>
            <form role="search" onSubmit={handleSearch} className="mt-8 flex flex-col gap-2 rounded-2xl bg-white p-2 shadow-lg ring-1 ring-primary/20 sm:flex-row">
              <label className="flex flex-1 items-center gap-2 rounded-xl px-3 focus-within:ring-2 focus-within:ring-primary">
                <SearchIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                <input
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Job title or keyword"
                  aria-label="Job title, keywords, or company"
                  type="text"
                  id="hero-term"
                  name="term"
                  className="w-full border-0 bg-transparent py-3 text-base text-gray-800 outline-none focus:ring-0"
                />
              </label>
              <label className="flex flex-1 items-center gap-2 rounded-xl px-3 focus-within:ring-2 focus-within:ring-primary sm:border-l sm:border-gray-100">
                <LocationMarkerIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                <input
                  onChange={(e) => setSearchLocation(e.target.value)}
                  placeholder="City or province"
                  aria-label="City, or Province"
                  type="text"
                  id="hero-location"
                  name="location"
                  className="w-full border-0 bg-transparent py-3 text-base text-gray-800 outline-none focus:ring-0"
                />
              </label>
              <button type="submit" className="rounded-xl bg-primary px-7 py-3 font-[Poppins] text-lg text-white transition hover:brightness-95">
                Find jobs
              </button>
            </form>
            {popular === null && (
              <div className="mt-4 flex min-h-[2rem] items-center gap-2 text-sm text-gray-500">
                Popular: <Spinner label="Loading popular searches…" className="h-4 w-4" />
              </div>
            )}
            {popular && popular.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-500">Popular:</span>
              {popular.map((term) => (
                <button
                  key={term}
                  type="button"
                  onClick={() => navigate(searchPath(term, searchLocation))}
                  className="rounded-full border border-primary/30 bg-white px-3 py-1 font-medium text-gray-700 transition hover:border-primary hover:text-primary-dark"
                >
                  {term}
                </button>
              ))}
            </div>
            )}
            <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-gray-700">
              {PROMISES.map((p) => (
                <li key={p} className="flex items-center gap-1.5">
                  <CheckCircleIcon className="h-5 w-5 text-primary-dark" aria-hidden="true" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="w-4/5 max-w-sm md:w-2/5 md:max-w-none">
            <div className="rounded-[2.5rem] bg-white p-4 shadow-xl ring-1 ring-primary/20 rotate-2">
              <img className="-rotate-2 rounded-[2rem] object-cover object-center" draggable="false" alt="" src={image} />
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="how-it-works" className="border-b border-primary/20 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-12">
          <h2 id="how-it-works" className="text-center font-display text-2xl font-bold text-gray-900">
            How it works
          </h2>
          <ol className="mt-8 grid gap-6 sm:grid-cols-3">
            {STEPS.map(([title, text], i) => (
              <li key={title} className="flex gap-4 rounded-2xl bg-tint p-5">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary font-[Poppins] text-white">{i + 1}</span>
                <div>
                  <p className="font-semibold text-gray-900">{title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </>
  );
}

